/* eslint-disable react-hooks/exhaustive-deps */
import { Container, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { LeftArrow } from "../../components/icons";
import Navbar from "../../components/navbar";
import Layout from "../../layout";
import { useBetween } from "use-between";
import useApi from "../useApi";
import { useEffect, useState } from "react";
import { toDateType, toTwoDecimals, formatPhoneNumber } from "../../plugins";
import AwareButton from "../../components/button";
import { toast } from "react-toastify";

import "./index.scss";

const OpenInvoice = () => {
    const params = useParams();
    const navigate = useNavigate();

    const {
        updating,
        customer,
        getOpenInvoiceByInvoiceId,
        setUpdating,
        getOrderByOrderNumber,
    } = useBetween(useApi);

    const [invoice, setInvoice] = useState(null);
    const [order, setOrder] = useState(null);
    const invoiceId = params.id;

    useEffect(() => {
        let isMounted = true;

        setUpdating(true);
        getOpenInvoiceByInvoiceId(invoiceId)
            .then((response) => {
                if (!isMounted) return;

                const invoiceDetail = response.openInvoiceDetail;
                setInvoice(invoiceDetail);
                setUpdating(false); // Invoice loaded, show the main UI

                const orderNumber = invoiceDetail.OrderNbr?.value;
                if (!orderNumber) {
                    return;
                }

                return getOrderByOrderNumber(orderNumber)
                    .then((orderResponse) => {
                        if (isMounted) {
                            setOrder(orderResponse.order);
                        }
                    });
            })
            .catch((error) => {
                if (isMounted) {
                    setUpdating(false);
                    toast.error('Sorry, we could not process this request. Please try again or contact your sales rep.');
                    navigate('/profile/open-invoices');
                }
            });

        return () => {
            isMounted = false;
        };
    }, [invoiceId]);

    const getStatusClass = (status) => {
        const statusLower = status?.toLowerCase();
        switch (statusLower) {
            case 'open':
                return 'status-open';
            case 'requested':
                return 'status-open';
            case 'paid':
                return 'status-completed';
            case 'on hold':
                return 'status-on-hold';
            case 'shipping':
                return 'status-shipping';
            case 'canceled':
                return 'status-canceled';
            default:
                return '';
        }
    };

    return (
        <Layout>
            <div className='bg-gray'>
                <Container className='open-invoice-detail-container d-flex'>
                    <Navbar active='open-invoice' />
                    <div className='open-invoice-detail-wrapper'>
                        <div className='back-button-wrapper mb-4'>
                            <AwareButton
                                className='btn btn-link p-0'
                                onClick={() => navigate('/profile/open-invoices')}
                            >
                                <LeftArrow width='20' height='20' />
                                <span className='ms-2'>Back to Invoices</span>
                            </AwareButton>
                        </div>

                        {updating ? (
                            <div className='loading-wrapper text-center py-5'>
                                <Spinner animation='border' role='status'>
                                    <span className='visually-hidden'>Loading...</span>
                                </Spinner>
                            </div>
                        ) : !invoice ? (
                            <div className='loading-wrapper text-center py-5'>
                                <Spinner animation='border' size='sm' className='me-2' />
                                <span>Loading invoice details...</span>
                            </div>
                        ) : (
                            <div className='invoice-detail-section'>
                                {/* Header */}
                                <div className='invoice-header'>
                                    <div className='header-top'>
                                        <div>
                                            <h3 className='invoice-title'>Invoice Details</h3>
                                            <p className='invoice-number'>
                                                Order Number #{invoice.OrderNbr?.value || 'N/A'}
                                            </p>
                                        </div>
                                        <div className={`invoice-status ${getStatusClass(invoice.Status?.value)}`}>
                                            {invoice.Status?.value || 'Unknown'}
                                        </div>

                                        <AwareButton
                                            onClick={() => navigate("/profile/open-invoices/" + invoice._id + "/schedule-payment-pickup")}
                                            className='btn btn-rebuild'
                                        >
                                            {invoice.PaymentPickup.length ? "Reschedule Payment Pickup" : "Schedule Payment Pickup"}
                                        </AwareButton>
                                    </div>
                                </div>

                                {/* Main Content */}
                                <div className='invoice-content'>
                                    {/* Summary Cards */}
                                    <div className='invoice-summary-cards'>
                                        <div className='summary-card'>
                                            <div className='card-icon amount-icon'>
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="2" y="5" width="20" height="14" rx="2" />
                                                    <line x1="2" y1="10" x2="22" y2="10" />
                                                </svg>
                                            </div>
                                            <div className='card-content'>
                                                <p className='card-label'>Total Amount</p>
                                                <h4 className='card-value'>
                                                    ${invoice.Amount?.value ? toTwoDecimals(Number(invoice.Amount.value)) : '0.00'}
                                                </h4>
                                            </div>
                                        </div>

                                        <div className='summary-card'>
                                            <div className='card-icon balance-icon'>
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <line x1="12" y1="8" x2="12" y2="16" />
                                                    <line x1="8" y1="12" x2="16" y2="12" />
                                                </svg>
                                            </div>
                                            <div className='card-content'>
                                                <p className='card-label'>Outstanding Balance</p>
                                                <h4 className='card-value'>
                                                    ${invoice.Balance?.value ? toTwoDecimals(Number(invoice.Balance.value)) : '0.00'}
                                                </h4>
                                            </div>
                                        </div>

                                        <div className='summary-card'>
                                            <div className='card-icon date-icon'>
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                            </div>
                                            <div className='card-content'>
                                                <p className='card-label'>Date Due</p>
                                                <h4 className='card-value'>
                                                    {invoice.DueDate?.value ? toDateType(invoice.DueDate.value) : 'N/A'}
                                                </h4>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Customer Information */}
                                    <div className='info-section'>
                                        <h5 className='section-title'>Customer Information</h5>
                                        <div className='info-grid'>
                                            <div className='info-item'>
                                                <span className='info-label'>Account Name</span>
                                                <span className='info-value'>{customer?.AccountRef || 'N/A'}</span>
                                            </div>
                                            <div className='info-item'>
                                                <span className='info-label'>Email</span>
                                                <span className='info-value'>{customer?.Email || 'N/A'}</span>
                                            </div>
                                            <div className='info-item'>
                                                <span className='info-label'>Phone</span>
                                                <span className='info-value'>
                                                    {customer?.Phone1 ? formatPhoneNumber(customer.Phone1) : 'N/A'}
                                                </span>
                                            </div>

                                        </div>
                                    </div>

                                    {/* Customer Information */}
                                    {/* Payment Pickup Information */}
                                    {invoice.PaymentPickup && invoice.PaymentPickup.length > 0 && (
                                        <div className='info-section'>
                                            <h5 className='section-title'>Payment Pickup Information</h5>
                                            <div className='payment-pickup-list'>
                                                {invoice.PaymentPickup.map((pickup, index) => (
                                                    <div key={index} className='payment-pickup-card'>
                                                        <div className='pickup-header'>
                                                            <span className='pickup-number'>Pickup #{index + 1}</span>
                                                            {pickup.Status && (
                                                                <span className={`pickup-status ${getStatusClass(pickup.Status)}`}>
                                                                    {pickup.Status}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className='info-grid'>

                                                            <div className='info-item'>
                                                                <span className='info-label'>Requested Date</span>
                                                                <span className='info-value'>{pickup.RequestedOn
                                                                    ? toDateType(pickup.RequestedOn) : 'N/A'}</span>
                                                            </div>


                                                            {pickup.Description?.value && (
                                                                <div className='info-item full-width'>
                                                                    <span className='info-label'>Description</span>
                                                                    <span className='info-value'>{pickup.Description.value}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Invoice Details */}
                                    <div className='info-section'>
                                        <h5 className='section-title'>Invoice Information</h5>
                                        <div className='info-grid'>
                                            <div className='info-item'>
                                                <span className='info-label'>Invoice Date</span>
                                                <span className='info-value'>
                                                    {invoice.Date?.value ? toDateType(invoice.Date.value) : 'N/A'}
                                                </span>
                                            </div>
                                            <div className='info-item'>
                                                <span className='info-label'>Date Due</span>
                                                <span className='info-value'>
                                                    {invoice.DueDate?.value ? toDateType(invoice.DueDate.value) : 'N/A'}
                                                </span>
                                            </div>
                                            <div className='info-item'>
                                                <span className='info-label'>Terms</span>
                                                <span className='info-value'>{invoice.Terms?.value || 'N/A'}</span>
                                            </div>
                                            <div className='info-item'>
                                                <span className='info-label'>Status</span>
                                                <span className={`info-value ${getStatusClass(invoice.Status?.value)}`}>
                                                    {invoice.Status?.value || 'Unknown'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Payment Details */}
                                    <div className='info-section'>
                                        <h5 className='section-title'>Payment Details</h5>
                                        <div className='payment-details-table'>

                                            <div className='payment-row total-row'>
                                                <span className='payment-label'>Total Amount</span>
                                                <span className='payment-value'>
                                                    ${invoice.Amount?.value ? toTwoDecimals(Number(invoice.Amount.value)) : '0.00'}
                                                </span>
                                            </div>
                                            <div className='payment-row balance-row'>
                                                <span className='payment-label'>Outstanding Balance</span>
                                                <span className='payment-value'>
                                                    ${invoice.Balance?.value ? toTwoDecimals(Number(invoice.Balance.value)) : '0.00'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Additional Notes */}
                                    {invoice.Description?.value && (
                                        <div className='info-section'>
                                            <h5 className='section-title'>Notes</h5>
                                            <div className='notes-content'>
                                                <p>{invoice.Description.value}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className='invoice-actions'>

                                        <AwareButton
                                            className='btn btn-primary'
                                            onClick={() => navigate('/profile/open-invoices')}
                                        >
                                            Back to Invoices
                                        </AwareButton>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Container>
            </div>
        </Layout>
    );
};

export default OpenInvoice;
